import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Modal, Button, Spinner, Alert } from 'react-bootstrap';
import ObjectConfigApi from '../../api/ObjectConfigApi';
import PicklistApi from '../../api/PicklistApi';
import EditRolePermissionModal from './EditRolePermissionModal';
import './css/AssignPicklistValuesModal.css';

function parsePicklistConfigSource(raw) {
  if (raw == null) return {};
  if (typeof raw === 'string') {
    try {
      return JSON.parse(raw) || {};
    } catch {
      return {};
    }
  }
  if (typeof raw === 'object' && !Array.isArray(raw)) return { ...raw };
  return {};
}

/** Full `config.picklists` for this object (only fields with a custom subset/order). Always returns `{ picklists }`. */
function buildConfigFromFieldState(fieldStates, picklistFieldNames) {
  const picklists = {};
  for (const fn of picklistFieldNames) {
    const st = fieldStates[fn];
    if (!st || !st.useCustom || !Array.isArray(st.orderedKeys) || st.orderedKeys.length === 0) {
      continue;
    }
    picklists[fn] = {
      allowed_keys: [...st.orderedKeys],
      display_order: [...st.orderedKeys],
    };
  }
  return { picklists };
}

/** Effective rows: custom subset/order or all active in Object Manager order. */
function getAssignedRowsForState(st, vals) {
  const byKey = new Map(vals.map((v) => [String(v.value_key), v]));
  if (st.useCustom && Array.isArray(st.orderedKeys) && st.orderedKeys.length > 0) {
    return st.orderedKeys.map((k) => byKey.get(String(k))).filter(Boolean);
  }
  return [...vals].sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
}

function formatAssignedValuesSummary(st, vals) {
  const rows = getAssignedRowsForState(st, vals);
  if (vals.length === 0) return '—';
  if (!st.useCustom) {
    return `All active (${rows.length})`;
  }
  if (rows.length === 0) return 'None selected';
  const labels = rows.map((r) => r.value_label || r.value_key).join(', ');
  const maxLen = 120;
  return labels.length > maxLen ? `${labels.slice(0, maxLen - 1)}…` : labels;
}

/**
 * Assign picklist / multipicklist values per object field (subset + order).
 * Used for role permissions (`permission.config.picklists`), record views (`field_config.picklists`), etc.
 */
const AssignPicklistValuesModal = ({
  show,
  onHide,
  permission,
  readOnly = false,
  onApply,
  /** Full title (replaces "Assign picklists — {object}"). Optional. */
  title,
  /** Muted helper text at top of body. Optional. */
  description,
  /** Block closing while parent persists (e.g. save in flight). */
  closeDisabled = false,
  /**
   * `simple` — single Close calls `onHide` (e.g. role permissions draft).
   * `saveOrDiscard` — Cancel / backdrop / X calls `onDiscardExit`; Save calls `onSaveExit` (e.g. record view).
   */
  exitMode = 'simple',
  onSaveExit,
  onDiscardExit,
}) => {
  const objectName = permission?.object_name;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [picklistFields, setPicklistFields] = useState([]);
  /** @type {Record<string, { values: Array<{ value_key: string, value_label: string, is_default?: boolean, sort_order?: number }> }>} */
  const [valuesByField, setValuesByField] = useState({});
  /** field_name -> { useCustom, orderedKeys } */
  const [fieldStates, setFieldStates] = useState({});
  /** Picklist field row being edited in sub-modal */
  const [editTarget, setEditTarget] = useState(null);

  const resetAndLoad = useCallback(async () => {
    if (!show || !objectName) return;
    setLoading(true);
    setError(null);
    setPicklistFields([]);
    setValuesByField({});
    setFieldStates({});
    setEditTarget(null);
    try {
      const res = await ObjectConfigApi.fetchConfigObjectByNameWithFields(objectName);
      const fields = Array.isArray(res?.data?.fields) ? res.data.fields : [];
      const plFields = fields.filter(
        (f) =>
          f &&
          (String(f.data_type).toLowerCase() === 'picklist' ||
            String(f.data_type).toLowerCase() === 'multipicklist')
      );
      setPicklistFields(plFields);

      const picklistApi = new PicklistApi();
      const valMap = {};
      await Promise.all(
        plFields.map(async (f) => {
          const fid = f.id;
          if (!fid) {
            valMap[f.field_name] = { values: [] };
            return;
          }
          try {
            const activeRes = await picklistApi.getActivePicklistValues(fid);
            const payload = activeRes?.data;
            const rows = Array.isArray(payload?.data) ? payload.data : [];
            valMap[f.field_name] = {
              values: rows.map((r) => ({
                value_key: String(r.value_key ?? ''),
                value_label: String(r.value_label ?? r.value_key ?? ''),
                is_default: r.is_default === true,
                sort_order: Number(r.sort_order) || 0,
              })),
            };
          } catch {
            valMap[f.field_name] = { values: [] };
          }
        })
      );
      setValuesByField(valMap);

      const cfg = parsePicklistConfigSource(permission?.config);
      const savedPicklists = cfg.picklists && typeof cfg.picklists === 'object' ? cfg.picklists : {};
      const initial = {};
      for (const f of plFields) {
        const fn = f.field_name;
        const allKeys = (valMap[fn]?.values || []).map((v) => v.value_key).filter(Boolean);
        const spec = savedPicklists[fn];
        const allowed = spec?.allowed_keys ?? spec?.allowedKeys;
        const order = spec?.display_order ?? spec?.displayOrder;
        const hasOverride =
          (Array.isArray(allowed) && allowed.length > 0) ||
          (Array.isArray(order) && order.length > 0);
        if (hasOverride) {
          const allowSet = Array.isArray(allowed) && allowed.length > 0 ? new Set(allowed.map(String)) : null;
          let keys = allowSet
            ? allKeys.filter((k) => allowSet.has(String(k)))
            : [...allKeys];
          if (Array.isArray(order) && order.length > 0) {
            const idx = new Map(order.map((k, i) => [String(k), i]));
            keys.sort((a, b) => {
              const ia = idx.has(String(a)) ? idx.get(String(a)) : 9999;
              const ib = idx.has(String(b)) ? idx.get(String(b)) : 9999;
              return ia - ib;
            });
          }
          initial[fn] = { useCustom: true, orderedKeys: keys };
        } else {
          initial[fn] = { useCustom: false, orderedKeys: [...allKeys] };
        }
      }
      setFieldStates(initial);
    } catch (e) {
      setError(e?.message || 'Failed to load object fields');
    } finally {
      setLoading(false);
    }
  }, [show, objectName, permission?.config]);

  useEffect(() => {
    resetAndLoad();
  }, [resetAndLoad]);

  const fieldNames = useMemo(() => picklistFields.map((f) => f.field_name).filter(Boolean), [picklistFields]);

  const handleEditSave = ({ useCustom, orderedKeys }) => {
    if (!editTarget) return;
    const fn = editTarget.field_name;
    setFieldStates((prev) => {
      const next = {
        ...prev,
        [fn]: { useCustom, orderedKeys: [...orderedKeys] },
      };
      if (onApply) {
        const nextConfig = buildConfigFromFieldState(next, fieldNames);
        queueMicrotask(() => onApply(objectName, nextConfig));
      }
      return next;
    });
  };

  const handleSimpleClose = () => {
    if (closeDisabled) return;
    setEditTarget(null);
    onHide();
  };

  const handleDiscardExit = () => {
    if (closeDisabled) return;
    setEditTarget(null);
    onDiscardExit?.();
  };

  const handleSaveExit = () => {
    if (closeDisabled) return;
    setEditTarget(null);
    onSaveExit?.();
  };

  const handleModalBackdropClose = () => {
    if (exitMode === 'saveOrDiscard') {
      handleDiscardExit();
    } else {
      handleSimpleClose();
    }
  };

  const displayTitle = permission?.object_display_name || objectName || 'Object';

  const editFieldApiName = editTarget?.field_name;
  const editVals = editFieldApiName ? valuesByField[editFieldApiName]?.values || [] : [];
  const editInitialState =
    editFieldApiName && fieldStates[editFieldApiName]
      ? fieldStates[editFieldApiName]
      : { useCustom: false, orderedKeys: [] };

  return (
    <>
      <Modal
        show={show}
        onHide={handleModalBackdropClose}
        size="lg"
        centered
        scrollable
        enforceFocus={false}
        restoreFocus={false}
        backdrop={closeDisabled ? 'static' : true}
        keyboard={!closeDisabled}
      >
        <Modal.Header closeButton={!closeDisabled}>
          <Modal.Title>
            {title ? (
              title
            ) : (
              <>
                Assign picklists — {displayTitle}
                {readOnly && <span className="text-muted fw-normal fs-6 ms-2">(view only)</span>}
              </>
            )}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {description ? <div className="text-muted small mb-3">{description}</div> : null}
          {loading && (
            <div className="d-flex align-items-center gap-2 py-4 justify-content-center">
              <Spinner animation="border" size="sm" />
              <span>Loading fields…</span>
            </div>
          )}
          {!loading && error && <Alert variant="danger">{error}</Alert>}
          {!loading && !error && picklistFields.length === 0 && (
            <p className="text-muted mb-0">This object has no picklist or multi-select fields.</p>
          )}
          {!loading && !error && picklistFields.length > 0 && (
            <div className="table-responsive">
              <table className="table table-sm align-middle mb-0">
                <thead>
                  <tr>
                    <th>Label</th>
                    <th className="text-muted">API name</th>
                    <th>Assigned values</th>
                    <th style={{ width: 56 }} className="text-end">
                      Action
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {picklistFields.map((f) => {
                    const fn = f.field_name;
                    const st = fieldStates[fn] || { useCustom: false, orderedKeys: [] };
                    const vals = valuesByField[fn]?.values || [];
                    const summary = formatAssignedValuesSummary(st, vals);
                    const fullList = getAssignedRowsForState(st, vals)
                      .map((r) => r.value_label || r.value_key)
                      .join(', ');
                    return (
                      <tr key={fn}>
                        <td className="fw-medium">{f.field_label || fn}</td>
                        <td className="text-muted small">{fn}</td>
                        <td>
                          <span className="small" title={fullList || summary}>
                            {summary}
                          </span>
                        </td>
                        <td className="text-end">
                          <button
                            type="button"
                            className="btn btn-link btn-sm p-0 assign-picklist-values-action-btn"
                            title={
                              readOnly
                                ? `View picklist values and order (${fn})`
                                : `Edit assigned values and order (${fn})`
                            }
                            aria-label={
                              readOnly
                                ? `View picklist ${f.field_label || fn}`
                                : `Edit picklist ${f.field_label || fn}`
                            }
                            onClick={() => setEditTarget(f)}
                          >
                            <i className="fas fa-edit" aria-hidden="true" />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          {exitMode === 'saveOrDiscard' ? (
            <>
              <Button variant="outline-secondary" onClick={handleDiscardExit} disabled={closeDisabled}>
                Cancel
              </Button>
              <Button variant="primary" onClick={handleSaveExit} disabled={closeDisabled}>
                {closeDisabled ? 'Saving…' : 'Save'}
              </Button>
            </>
          ) : (
            <Button variant="secondary" onClick={handleSimpleClose} disabled={closeDisabled}>
              Close
            </Button>
          )}
        </Modal.Footer>
      </Modal>

      <EditRolePermissionModal
        show={editTarget != null}
        onHide={() => setEditTarget(null)}
        fieldLabel={editTarget?.field_label || editFieldApiName}
        fieldApiName={editFieldApiName || ''}
        values={editVals}
        initialState={editInitialState}
        readOnly={readOnly}
        onSave={readOnly ? undefined : handleEditSave}
      />
    </>
  );
};

export default AssignPicklistValuesModal;
